import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { appRoutes } from '@/routes';
import { Container, Alert, Spinner, Label, FormGroup, Input } from 'reactstrap';
import { Input as CustomInput } from '@components/Input';
import { Button as CustomButton } from '@components/Button';
// import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button as PrimeButton } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Menu } from 'primereact/menu';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import firebase, { db } from '../../config/firebase.config';
import httpClient from '../../config/httpClient';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import moment from 'moment-timezone';
import { useFormChanges } from '../../hooks/useFormChanges';
import { useStealthMode } from '../../hooks/useStealthMode';
import { useUserCompanies } from '../../hooks/useUserCompanies';
import { useUserTeamsForCompany } from '../../hooks/useUserTeamsForCompany';
import { useEmployeeListRoster } from '../../hooks/useEmployeeListRoster';
import {
  fetchUserTeamsForCompany,
  fetchUserCompanies,
} from '../../services/userMembership';
import teamDatabase from '@services/team';
import { fetchEmployeeExtendedDetails } from '../../services/employeeMemberEnrichment';
import { manageTeamMembersActionTypes } from '@store/ManageTeams/ManageTeams.actionTypes';
import { queryKeys } from '../../config/queryKeys';
import manageTeamsService from '../../services/manageTeams';
import { Text, fontWeightEnum, textVariantsEnum } from '@/components/Text';
import styles from './EmployeeList.module.scss';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { ButtonComponent } from '@/components/Button/Button.component';
import { buttonVariants } from '@/components/Button';
import { AddTeamModal } from '@/views/Teams/components/TeamsTab/AddTeamModal.component';
import authService from '@services/auth';
import { useEmployeePageCompanyTeamUrlSync } from '@/hooks/useEmployeePageUrlSync';
import { mergeEmployeePageFiltersIntoSearch } from '@/utils/employeePageUrlFilters';
import { IntegrationImportTab } from './components/IntegrationImportTab.component';

const ROLE_OPTIONS = [
  { label: 'Member', value: 'Member' },
  { label: 'Manager', value: 'Manager' },
  { label: 'Admin', value: 'Admin' },
];

export const EmployeeListComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const authState = useSelector((state) => ({ ...state.auth }));
  const userProfile = authState.profile;
  const activeCompanyId = userProfile?.activeCompany?.companyId;
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );
  const userRole = userProfile?.role || '';
  const showAddTeamButton = userRole === 'Admin';
  const companyList = userProfile?.companyList || [];
  const userUID = userProfile?.id;
  const toast = React.useRef(null);
  const menuRef = useRef(null);
  const editFormHydratedRef = useRef(null);
  /** Set when saving a role change under stealth; consumed on UPDATE_EMPLOYEE_DETAILS success. */
  const pendingStealthAccessSyncRef = useRef(null);
  const {
    updateEmployeeDetailsSuccess,
    updateEmployeeDetailsPending,
    updateEmployeeDetailsFailure,
    updateEmployeeDetailsFailurePayload,
    removeUserFromTeamSuccess,
    removeUserFromTeamPending,
    removeUserFromTeamFailure,
    removeUserFromTeamFailurePayload,
  } = useSelector((state) => state.manageTeams?.members || {});

  // State
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [error, setError] = useState(null);
  const [moreDetailsUserId, setMoreDetailsUserId] = useState(null);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDeskId, setEditDeskId] = useState('');
  const [editTimeZone, setEditTimeZone] = useState('');
  const [editRole, setEditRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('employees');
  const [deleteFlowLoading, setDeleteFlowLoading] = useState(false);
  const [pickActiveTeamDialogVisible, setPickActiveTeamDialogVisible] =
    useState(false);
  const [replacementTeamOptions, setReplacementTeamOptions] = useState([]);
  const [selectedReplacementTeam, setSelectedReplacementTeam] = useState(null);
  const [pendingDeleteContext, setPendingDeleteContext] = useState(null);
  const [removeFromCompanyPending, setRemoveFromCompanyPending] =
    useState(false);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [teamListRefreshKey, setTeamListRefreshKey] = useState(0);
  const [addToAnotherTeamDialogVisible, setAddToAnotherTeamDialogVisible] =
    useState(false);
  const [addToAnotherTeamUser, setAddToAnotherTeamUser] = useState(null);
  const [addToAnotherTeamCompany, setAddToAnotherTeamCompany] = useState(null);
  const [addToAnotherTeamRole, setAddToAnotherTeamRole] = useState(null);
  const [addToAnotherTeamTeams, setAddToAnotherTeamTeams] = useState([]);
  const [addToAnotherTeamCandidateTeams, setAddToAnotherTeamCandidateTeams] =
    useState([]);
  const [
    addToAnotherTeamCompanyTeamCount,
    setAddToAnotherTeamCompanyTeamCount,
  ] = useState(0);
  const [
    loadingAddToAnotherTeamCandidates,
    setLoadingAddToAnotherTeamCandidates,
  ] = useState(false);
  const [submittingAddToAnotherTeam, setSubmittingAddToAnotherTeam] =
    useState(false);
  const [
    addToAnotherTeamActiveCompanyOptions,
    setAddToAnotherTeamActiveCompanyOptions,
  ] = useState([]);
  const [addToAnotherTeamActiveCompany, setAddToAnotherTeamActiveCompany] =
    useState(null);
  const [
    addToAnotherTeamActiveTeamOptions,
    setAddToAnotherTeamActiveTeamOptions,
  ] = useState([]);
  const [addToAnotherTeamActiveTeam, setAddToAnotherTeamActiveTeam] =
    useState(null);
  const [addToAnotherTeamActiveBaseline, setAddToAnotherTeamActiveBaseline] =
    useState(null);
  const [
    loadingAddToAnotherTeamActiveSection,
    setLoadingAddToAnotherTeamActiveSection,
  ] = useState(false);

  const [changeActiveTeamDialogVisible, setChangeActiveTeamDialogVisible] =
    useState(false);
  const [changeActiveTeamUser, setChangeActiveTeamUser] = useState(null);
  const [changeActiveTeamCompanyOptions, setChangeActiveTeamCompanyOptions] =
    useState([]);
  const [changeActiveTeamCompany, setChangeActiveTeamCompany] = useState(null);
  const [changeActiveTeamTeamOptions, setChangeActiveTeamTeamOptions] =
    useState([]);
  const [changeActiveTeamTeam, setChangeActiveTeamTeam] = useState(null);
  const [loadingChangeActiveTeamModal, setLoadingChangeActiveTeamModal] =
    useState(false);
  const [submittingChangeActiveTeam, setSubmittingChangeActiveTeam] =
    useState(false);
  const [changeActiveTeamBaseline, setChangeActiveTeamBaseline] =
    useState(null);

  const addTeamDefaultCompanyId = useMemo(
    () => selectedCompany?.value ?? activeCompanyId,
    [selectedCompany?.value, activeCompanyId]
  );

  const { stealthMode, loading: stealthLoading } = useStealthMode(
    selectedCompany?.value
  );

  const timezoneOptions = moment.tz
    .names()
    .map((tz) => ({ label: tz, value: tz }));

  const {
    employees,
    isLoading: loadingEmployees,
    isError: rosterError,
    error: rosterQueryError,
  } = useEmployeeListRoster(selectedTeam?.value);

  const userIdForExtendedFetch = moreDetailsUserId
    ? moreDetailsUserId
    : editDialogVisible && editRow?.userId
      ? editRow.userId
      : null;

  const {
    data: extendedDetails,
    isLoading: loadingExtendedDetails,
    isError: extendedDetailsError,
    error: extendedDetailsQueryError,
  } = useQuery({
    queryKey: queryKeys.employeeList.extendedDetails(userIdForExtendedFetch),
    queryFn: async () => {
      const data = await fetchEmployeeExtendedDetails(
        db,
        userIdForExtendedFetch
      );
      if (!data) {
        throw new Error('Member not found');
      }
      return data;
    },
    enabled: Boolean(userIdForExtendedFetch),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const invalidateEmployeeListData = useCallback(() => {
    if (selectedTeam?.value) {
      queryClient.invalidateQueries(
        queryKeys.teamMemberDetails.detail(selectedTeam.value)
      );
    }
    if (editRow?.userId) {
      queryClient.invalidateQueries(
        queryKeys.employeeList.extendedDetails(editRow.userId)
      );
    }
  }, [queryClient, selectedTeam?.value, editRow?.userId]);

  useLayoutEffect(() => {
    if (!editDialogVisible || !editRow?.userId || !extendedDetails) return;
    if (editFormHydratedRef.current === editRow.userId) return;
    editFormHydratedRef.current = editRow.userId;
    setEditName(
      editRow.name === 'N/A' || editRow.name == null ? '' : String(editRow.name)
    );
    setEditDeskId(
      extendedDetails.deskId === 'N/A' || !extendedDetails.deskId
        ? ''
        : String(extendedDetails.deskId)
    );
    setEditTimeZone(
      extendedDetails.timeZone === 'N/A' ? '' : String(extendedDetails.timeZone)
    );
    setEditRole(editRow.role === 'N/A' ? '' : editRow.role);
  }, [
    editDialogVisible,
    editRow?.userId,
    extendedDetails,
    editRow?.role,
    editRow?.name,
  ]);

  const {
    companyOptions,
    loading: loadingCompanies,
    error: companiesError,
  } = useUserCompanies(userUID);
  const {
    teamOptions,
    loading: loadingTeams,
    error: teamsError,
  } = useUserTeamsForCompany(
    userUID,
    selectedCompany?.value,
    teamListRefreshKey
  );

  useEmployeePageCompanyTeamUrlSync({
    companyOptions,
    teamOptions,
    selectedCompany,
    selectedTeam,
    setSelectedCompany,
    setSelectedTeam,
    history,
    location,
    defaultCompanyId: activeCompanyId,
    defaultTeamId: activeTeamId,
    teamDefaultOnlyWhenCompanyId: activeCompanyId,
  });

  const dispatchRemoveUserFromTeam = useCallback(
    (payload) => {
      dispatch({
        type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM,
        payload,
      });
    },
    [dispatch]
  );

  const handleTeamCreated = useCallback(() => {
    setTeamListRefreshKey((k) => k + 1);
  }, []);

  const showDeleteConfirm = useCallback(
    ({ message, payload }) => {
      confirmDialog({
        message,
        header: 'Delete user',
        icon: 'pi pi-exclamation-triangle',
        acceptClassName: 'p-button-danger',
        accept: () => dispatchRemoveUserFromTeam(payload),
      });
    },
    [dispatchRemoveUserFromTeam]
  );

  const getMemberActiveSettings = useCallback(async (userId) => {
    const memberDoc = await db.collection('membersDatabase').doc(userId).get();
    if (!memberDoc.exists) return { activeCompanyId: null, activeTeamId: null };
    const data = memberDoc.data() || {};
    return {
      activeCompanyId: data.activeCompanyId || null,
      activeTeamId: data.activeTeamId || null,
    };
  }, []);

  const getMemberTeamsForCompany = useCallback(async (userId, companyId) => {
    const res = await fetchUserTeamsForCompany(userId, companyId);
    if (!res.success) return [];
    return res.data;
  }, []);

  const buildTeamOptions = useCallback((teams) => {
    return (teams || [])
      .filter(Boolean)
      .map((t) => ({ label: t.teamName || t.teamId, value: t.teamId }));
  }, []);

  const resetPickActiveTeamDialog = useCallback(() => {
    setPickActiveTeamDialogVisible(false);
    setReplacementTeamOptions([]);
    setSelectedReplacementTeam(null);
    setPendingDeleteContext(null);
  }, []);

  const resetAddToAnotherTeamDialog = useCallback(() => {
    setAddToAnotherTeamDialogVisible(false);
    setAddToAnotherTeamUser(null);
    setAddToAnotherTeamRole(null);
    setAddToAnotherTeamTeams([]);
    setAddToAnotherTeamCandidateTeams([]);
    setAddToAnotherTeamCompanyTeamCount(0);
    setLoadingAddToAnotherTeamCandidates(false);
    setSubmittingAddToAnotherTeam(false);
    setAddToAnotherTeamActiveCompanyOptions([]);
    setAddToAnotherTeamActiveCompany(null);
    setAddToAnotherTeamActiveTeamOptions([]);
    setAddToAnotherTeamActiveTeam(null);
    setAddToAnotherTeamActiveBaseline(null);
    setLoadingAddToAnotherTeamActiveSection(false);
  }, []);

  const resetChangeActiveTeamDialog = useCallback(() => {
    setChangeActiveTeamBaseline(null);
    setChangeActiveTeamDialogVisible(false);
    setChangeActiveTeamUser(null);
    setChangeActiveTeamCompanyOptions([]);
    setChangeActiveTeamCompany(null);
    setChangeActiveTeamTeamOptions([]);
    setChangeActiveTeamTeam(null);
    setLoadingChangeActiveTeamModal(false);
    setSubmittingChangeActiveTeam(false);
  }, []);

  const loadTeamsForChangeActiveTeam = useCallback(
    async (memberUserId, companyId, preferredTeamId) => {
      const teams = await getMemberTeamsForCompany(memberUserId, companyId);
      const teamOpts = buildTeamOptions(teams);
      setChangeActiveTeamTeamOptions(teamOpts);
      if (!teamOpts.length) {
        setChangeActiveTeamTeam(null);
        return { teamOpts, selectedTeamValue: null };
      }
      const pick =
        preferredTeamId && teamOpts.some((t) => t.value === preferredTeamId)
          ? preferredTeamId
          : teamOpts[0].value;
      const selected = teamOpts.find((t) => t.value === pick) || teamOpts[0];
      setChangeActiveTeamTeam(selected);
      return { teamOpts, selectedTeamValue: selected.value };
    },
    [getMemberTeamsForCompany, buildTeamOptions]
  );

  const openChangeActiveTeamDialog = useCallback(async () => {
    if (!editRow?.userId) {
      addNotification({
        message: 'Missing user context',
        level: notificationStates.warning,
      });
      return;
    }
    menuRef.current?.hide();
    const rowUserId = editRow.userId;
    setChangeActiveTeamUser({
      userId: rowUserId,
      email: editRow.email,
      name: editRow.name,
    });
    setChangeActiveTeamDialogVisible(true);
    setLoadingChangeActiveTeamModal(true);
    setChangeActiveTeamCompany(null);
    setChangeActiveTeamTeam(null);
    setChangeActiveTeamCompanyOptions([]);
    setChangeActiveTeamTeamOptions([]);
    setChangeActiveTeamBaseline(null);

    try {
      const [activeSettings, companiesRes] = await Promise.all([
        getMemberActiveSettings(rowUserId),
        fetchUserCompanies(rowUserId),
      ]);

      if (!companiesRes.success || !companiesRes.data?.length) {
        addNotification({
          message:
            companiesRes.error || 'This member has no companies on file.',
          level: notificationStates.warning,
        });
        resetChangeActiveTeamDialog();
        return;
      }

      const companyOpts = companiesRes.data.map((c) => ({
        label: c.companyName || c.companyId,
        value: c.companyId,
      }));
      setChangeActiveTeamCompanyOptions(companyOpts);

      const validIds = new Set(companyOpts.map((o) => o.value));
      const fromMemberDoc =
        activeSettings.activeCompanyId &&
        validIds.has(activeSettings.activeCompanyId)
          ? activeSettings.activeCompanyId
          : null;
      const fromPage =
        selectedCompany?.value && validIds.has(selectedCompany.value)
          ? selectedCompany.value
          : null;
      const companyId = fromMemberDoc || fromPage || companyOpts[0].value;
      const companyOption =
        companyOpts.find((o) => o.value === companyId) || companyOpts[0];
      setChangeActiveTeamCompany(companyOption);

      const { selectedTeamValue } = await loadTeamsForChangeActiveTeam(
        rowUserId,
        companyOption.value,
        activeSettings.activeTeamId
      );
      setChangeActiveTeamBaseline({
        companyId: companyOption.value,
        teamId: selectedTeamValue,
      });
    } catch (e) {
      addNotification({
        message: e?.message || 'Failed to load active team settings',
        level: notificationStates.error,
      });
      resetChangeActiveTeamDialog();
    } finally {
      setLoadingChangeActiveTeamModal(false);
    }
  }, [
    editRow,
    selectedCompany?.value,
    getMemberActiveSettings,
    loadTeamsForChangeActiveTeam,
    resetChangeActiveTeamDialog,
  ]);

  const onChangeActiveTeamCompanySelect = useCallback(
    async (option) => {
      if (!changeActiveTeamUser?.userId || !option?.value) return;
      setChangeActiveTeamCompany(option);
      setChangeActiveTeamTeam(null);
      setLoadingChangeActiveTeamModal(true);
      try {
        await loadTeamsForChangeActiveTeam(
          changeActiveTeamUser.userId,
          option.value,
          null
        );
      } finally {
        setLoadingChangeActiveTeamModal(false);
      }
    },
    [changeActiveTeamUser?.userId, loadTeamsForChangeActiveTeam]
  );

  const submitChangeActiveTeam = useCallback(async () => {
    if (
      !changeActiveTeamUser?.userId ||
      !changeActiveTeamCompany?.value ||
      !changeActiveTeamTeam?.value
    ) {
      addNotification({
        message: 'Select company and active team',
        level: notificationStates.warning,
      });
      return;
    }
    setSubmittingChangeActiveTeam(true);
    try {
      const updateRes = await teamDatabase.updateMemberActiveCompanyAndTeam(
        changeActiveTeamUser.userId,
        changeActiveTeamCompany.value,
        changeActiveTeamTeam.value
      );
      if (!updateRes?.success) {
        throw new Error(
          updateRes?.error?.message ||
            updateRes?.error ||
            'Could not save active team'
        );
      }
      addNotification({
        message: `Active team saved: ${changeActiveTeamTeam.label}`,
        level: notificationStates.success,
      });
      const emailRes = await teamDatabase.sendActiveTeamChangeEmail({
        companyName: changeActiveTeamCompany.label || '',
        teamName: changeActiveTeamTeam.label || '',
      });
      if (!emailRes?.success) {
        addNotification({
          message:
            'Active team saved, but the confirmation email could not be sent.',
          level: notificationStates.warning,
        });
      }
      queryClient.invalidateQueries(
        queryKeys.employeeList.extendedDetails(changeActiveTeamUser.userId)
      );
      invalidateEmployeeListData();
      resetChangeActiveTeamDialog();
    } catch (e) {
      addNotification({
        message: e?.message || 'Failed to update active team',
        level: notificationStates.error,
      });
    } finally {
      setSubmittingChangeActiveTeam(false);
    }
  }, [
    changeActiveTeamUser,
    changeActiveTeamCompany,
    changeActiveTeamTeam,
    queryClient,
    invalidateEmployeeListData,
    resetChangeActiveTeamDialog,
  ]);

  const hasChangeActiveTeamPendingSave = useMemo(() => {
    if (
      !changeActiveTeamBaseline?.companyId ||
      !changeActiveTeamCompany?.value ||
      !changeActiveTeamTeam?.value
    ) {
      return false;
    }
    const companyChanged =
      changeActiveTeamCompany.value !== changeActiveTeamBaseline.companyId;
    const baselineTeam = changeActiveTeamBaseline.teamId ?? '';
    const currentTeam = changeActiveTeamTeam.value ?? '';
    const teamChanged = String(currentTeam) !== String(baselineTeam);
    return companyChanged || teamChanged;
  }, [changeActiveTeamBaseline, changeActiveTeamCompany, changeActiveTeamTeam]);

  const loadAddToAnotherTeamCandidates = useCallback(
    async ({ companyId, userId }) => {
      if (!companyId || !userId) {
        setAddToAnotherTeamCandidateTeams([]);
        setAddToAnotherTeamCompanyTeamCount(0);
        return;
      }

      setLoadingAddToAnotherTeamCandidates(true);
      try {
        const [companyTeamsRes, memberTeamsRes] = await Promise.all([
          fetchUserTeamsForCompany(userUID, companyId),
          fetchUserTeamsForCompany(userId, companyId),
        ]);

        if (!companyTeamsRes.success) {
          throw new Error(companyTeamsRes.message || 'Failed to load teams');
        }
        if (!memberTeamsRes.success) {
          throw new Error(
            memberTeamsRes.message || 'Failed to load member team list'
          );
        }

        const allCompanyTeams = companyTeamsRes.data || [];
        const memberTeams = memberTeamsRes.data || [];
        const memberTeamIds = new Set(memberTeams.map((t) => t.teamId));
        const candidateTeams = allCompanyTeams
          .filter((t) => !memberTeamIds.has(t.teamId))
          .map((t) => ({ label: t.teamName || t.teamId, value: t.teamId }));

        setAddToAnotherTeamCompanyTeamCount(allCompanyTeams.length);
        setAddToAnotherTeamCandidateTeams(candidateTeams);
        setAddToAnotherTeamTeams([]);
      } catch (e) {
        setAddToAnotherTeamCandidateTeams([]);
        setAddToAnotherTeamCompanyTeamCount(0);
        addNotification({
          message:
            e?.message || 'Failed to load teams for "Add to another team".',
          level: notificationStates.error,
        });
      } finally {
        setLoadingAddToAnotherTeamCandidates(false);
      }
    },
    [userUID]
  );

  // Filter roster (name, email, role from memberDetails)
  const filteredEmployees = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((emp) => {
      const name = (emp.name || '').toLowerCase();
      const email = (emp.email || '').toLowerCase();
      const role = (emp.role || '').toLowerCase();
      return name.includes(term) || email.includes(term) || role.includes(term);
    });
  }, [employees, searchTerm]);

  const moreDetailsRosterRow = useMemo(() => {
    if (!moreDetailsUserId) return null;
    if (editRow?.userId === moreDetailsUserId) return editRow;
    return employees.find((e) => e.userId === moreDetailsUserId) || null;
  }, [moreDetailsUserId, editRow, employees]);

  // Check if user is Admin
  useEffect(() => {
    const isAdmin =
      userProfile?.activeCompany?.role === 'Admin' ||
      companyList.some((company) => company.role === 'Admin');

    if (!isAdmin && !authState.loading) {
      setError('Access denied. Only Admin users can view employee list.');
    }
  }, [userProfile, companyList, authState.loading]);

  // When company is cleared, reset team (roster comes from React Query by teamId)
  useEffect(() => {
    if (!selectedCompany?.value) {
      setSelectedTeam(null);
    }
  }, [selectedCompany?.value]);

  // On update employee details success: close dialog, cleanup, invalidate queries
  useEffect(() => {
    if (updateEmployeeDetailsSuccess) {
      const stealthSyncPending = pendingStealthAccessSyncRef.current;
      pendingStealthAccessSyncRef.current = null;

      setEditDialogVisible(false);
      setEditRow(null);
      setEditName('');
      setEditDeskId('');
      setEditTimeZone('');
      setEditRole('');
      editFormHydratedRef.current = null;
      dispatch({
        type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS_CLEANUP,
      });
      invalidateEmployeeListData();
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Employee details updated successfully',
          life: 3000,
        });
      }

      if (stealthSyncPending) {
        const { userId, companyId, emailForPasswordReset } = stealthSyncPending;
        (async () => {
          try {
            const user = firebase.auth().currentUser;
            if (!user) return;
            const idToken = await user.getIdToken();
            const res = await httpClient.post(
              '/syncEmployeeAccessForCompany',
              { userId, companyId },
              { headers: { Authorization: `Bearer ${idToken}` } }
            );
            const accessSync = res.data?.data?.accessSync;
            if (accessSync?.action === 'granted' && emailForPasswordReset) {
              await authService.sendPasswordResetEmail({
                email: emailForPasswordReset,
              });
              if (toast.current) {
                toast.current.show({
                  severity: 'success',
                  summary: 'Access granted',
                  detail: 'Password reset email sent to the employee.',
                  life: 4000,
                });
              }
            } else if (accessSync?.action === 'revoked' && toast.current) {
              toast.current.show({
                severity: 'info',
                summary: 'Login disabled',
                detail: 'Login has been disabled for this member.',
                life: 4000,
              });
            }
          } catch (e) {
            if (toast.current) {
              toast.current.show({
                severity: 'error',
                summary: 'Access sync',
                detail:
                  e.response?.data?.message ||
                  e.message ||
                  'Failed to sync login access.',
                life: 4000,
              });
            }
          }
        })();
      }
    }
  }, [updateEmployeeDetailsSuccess, dispatch, invalidateEmployeeListData]);
  // Show error when update fails
  useEffect(() => {
    if (
      updateEmployeeDetailsFailure &&
      updateEmployeeDetailsFailurePayload?.message
    ) {
      pendingStealthAccessSyncRef.current = null;
      addNotification({
        message: updateEmployeeDetailsFailurePayload.message,
        level: notificationStates.error,
      });
      dispatch({
        type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS_CLEANUP,
      });
    }
  }, [
    updateEmployeeDetailsFailure,
    updateEmployeeDetailsFailurePayload,
    dispatch,
  ]);

  // On remove user from team success: cleanup, invalidate roster, toast
  useEffect(() => {
    if (removeUserFromTeamSuccess) {
      dispatch({
        type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM_CLEANUP,
      });
      invalidateEmployeeListData();
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'User removed from team',
          life: 3000,
        });
      }
    }
  }, [removeUserFromTeamSuccess, dispatch, invalidateEmployeeListData]);

  // Show error when remove user fails
  useEffect(() => {
    if (
      removeUserFromTeamFailure &&
      removeUserFromTeamFailurePayload?.message
    ) {
      addNotification({
        message: removeUserFromTeamFailurePayload.message,
        level: notificationStates.error,
      });
      dispatch({
        type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM_CLEANUP,
      });
    }
  }, [removeUserFromTeamFailure, removeUserFromTeamFailurePayload, dispatch]);

  // Copy GUID to clipboard
  const copyGuid = (guid) => {
    if (guid === 'N/A' || !guid) {
      return;
    }

    navigator.clipboard
      .writeText(guid)
      .then(() => {
        if (toast.current) {
          toast.current.show({
            severity: 'success',
            summary: 'Copied',
            detail: 'GUID copied to clipboard',
            life: 2000,
          });
        }
      })
      .catch((err) => {
        console.error('Failed to copy GUID:', err);
        addNotification({
          message: 'Failed to copy GUID',
          level: notificationStates.error,
        });
      });
  };

  // Escape CSV field (handles quotes and commas)
  const escapeCSVField = (field) => {
    if (field === null || field === undefined) {
      return '';
    }
    const stringField = String(field);
    // If field contains comma, quote, or newline, wrap in quotes and escape quotes
    if (
      stringField.includes(',') ||
      stringField.includes('"') ||
      stringField.includes('\n')
    ) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }
    return stringField;
  };

  // Export to CSV (exports current view: filtered list when search is active)
  const exportToCSV = () => {
    if (filteredEmployees.length === 0) {
      addNotification({
        message: searchTerm.trim()
          ? 'No data to export (no employees match your search).'
          : 'No data to export',
        level: notificationStates.warning,
      });
      return;
    }

    // Get company and team names for filename
    const companyName =
      companyOptions.find((c) => c.value === selectedCompany?.value)?.label ||
      'company';
    const teamName =
      teamOptions.find((t) => t.value === selectedTeam?.value)?.label || 'team';
    const date = new Date().toISOString().split('T')[0];
    const filename =
      `employee-list-${companyName}-${teamName}-${date}.csv`.replace(
        /[^a-z0-9-]/gi,
        '-'
      );

    // Roster export: memberDetails fields only (same as table)
    const headers = ['Name', 'Email', 'Role'];
    const csvRows = [
      headers.join(','),
      ...filteredEmployees.map((emp) =>
        [
          escapeCSVField(emp.name),
          escapeCSVField(emp.email),
          escapeCSVField(emp.role),
        ].join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');
    // Add BOM for Excel compatibility with UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    addNotification({
      message: 'CSV exported successfully',
      level: notificationStates.success,
    });
  };

  const onActionsButtonClick = (e, rowData) => {
    setEditRow(rowData);
    menuRef.current.toggle(e);
  };

  const openMoreDetailsDialog = useCallback(() => {
    if (editRow?.userId) setMoreDetailsUserId(editRow.userId);
    menuRef.current?.hide();
  }, [editRow?.userId]);

  const openEditDialog = useCallback(() => {
    editFormHydratedRef.current = null;
    setEditDialogVisible(true);
    menuRef.current?.hide();
  }, []);

  const openDeleteConfirm = async () => {
    if (
      !editRow?.userId ||
      !selectedCompany?.value ||
      !selectedTeam?.value ||
      deleteFlowLoading
    )
      return;

    menuRef.current.hide();
    setDeleteFlowLoading(true);

    const userId = editRow.userId;
    const companyId = selectedCompany.value;
    const teamId = selectedTeam.value;

    try {
      const [teamsInCompany, active] = await Promise.all([
        getMemberTeamsForCompany(userId, companyId),
        getMemberActiveSettings(userId),
      ]);

      // Rule (1): cannot remove if user is only in one team (in this company)
      if (!teamsInCompany || teamsInCompany.length <= 1) {
        addNotification({
          message:
            'This user is only in one team. Add them to another team first, then delete.',
          level: notificationStates.warning,
        });
        return;
      }

      const isDeletingFromActiveTeam =
        active?.activeCompanyId === companyId &&
        active?.activeTeamId === teamId;

      // Rules (3) and (4): deleting from a non-active team keeps active team unchanged
      if (!isDeletingFromActiveTeam) {
        showDeleteConfirm({
          message: 'Are you sure you want to delete this user from the team?',
          payload: { userId, companyId, teamId },
        });
        return;
      }

      const remainingTeams = teamsInCompany.filter((t) => t.teamId !== teamId);

      // Safety: should never happen if teamsInCompany.length > 1, but keep it defensive
      if (remainingTeams.length === 0) {
        addNotification({
          message:
            'Cannot delete user from their only team. Add them to another team first.',
          level: notificationStates.warning,
        });
        return;
      }

      // Rule (2): exactly two teams -> auto-switch active to the other team
      if (remainingTeams.length === 1) {
        const newActiveTeamId = remainingTeams[0].teamId;
        showDeleteConfirm({
          message:
            'This user is being removed from their active team. The other team will be set as active. Continue?',
          payload: { userId, companyId, teamId, newActiveTeamId },
        });
        return;
      }

      // Rule (5): multiple remaining teams -> ask which team should become active
      const options = buildTeamOptions(remainingTeams);
      setReplacementTeamOptions(options);
      setSelectedReplacementTeam(options[0] || null);
      setPendingDeleteContext({
        userId,
        companyId,
        teamId,
      });
      setPickActiveTeamDialogVisible(true);
    } catch (e) {
      console.error(
        '[EmployeeList delete] failed to build delete strategy:',
        e
      );
      addNotification({
        message:
          e?.message || 'Failed to prepare delete action. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setDeleteFlowLoading(false);
    }
  };

  const confirmDeleteAfterPickingActiveTeam = useCallback(() => {
    if (
      !pendingDeleteContext?.userId ||
      !pendingDeleteContext?.companyId ||
      !pendingDeleteContext?.teamId
    ) {
      resetPickActiveTeamDialog();
      return;
    }
    if (!selectedReplacementTeam?.value) {
      addNotification({
        message: 'Please select which team should be made active.',
        level: notificationStates.warning,
      });
      return;
    }

    const payload = {
      ...pendingDeleteContext,
      newActiveTeamId: selectedReplacementTeam.value,
    };

    resetPickActiveTeamDialog();
    showDeleteConfirm({
      message:
        'This user is being removed from their active team. The selected team will be set as active. Continue?',
      payload,
    });
  }, [
    pendingDeleteContext,
    selectedReplacementTeam,
    resetPickActiveTeamDialog,
    showDeleteConfirm,
  ]);

  const openRemoveFromCompanyConfirm = () => {
    if (!editRow?.userId || !selectedCompany?.value || removeFromCompanyPending)
      return;
    menuRef.current?.hide();
    const companyName =
      companyOptions.find((c) => c.value === selectedCompany.value)?.label ||
      selectedCompany.label ||
      'this company';
    const userName =
      editRow.name && editRow.name !== 'N/A' ? editRow.name : editRow.email;
    confirmDialog({
      message: `Remove ${userName} from ${companyName}? They will be removed from all teams in this company and their license will be revoked.`,
      header: 'Remove from company',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        setRemoveFromCompanyPending(true);
        try {
          if (
            !userProfile?.subscription?.isTrialActive &&
            userProfile?.subscription?.status !== 'active'
          ) {
            addNotification({
              message:
                'Your subscription is not active. Please activate your subscription to remove employees.',
              level: notificationStates.error,
            });
            return;
          }
          const result = await manageTeamsService.removeUserFromCompany({
            userId: editRow.userId,
            companyId: selectedCompany.value,
          });
          if (result.success) {
            invalidateEmployeeListData();
            if (toast.current) {
              toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'User removed from company and all its teams',
                life: 3000,
              });
            }
          } else {
            addNotification({
              message: result.message || 'Failed to remove user from company',
              level: notificationStates.error,
            });
          }
        } catch (e) {
          addNotification({
            message: e?.message || 'Failed to remove user from company',
            level: notificationStates.error,
          });
        } finally {
          setRemoveFromCompanyPending(false);
        }
      },
    });
  };

  const openAddToAnotherTeamDialog = useCallback(() => {
    if (!editRow?.userId || !editRow?.email) {
      addNotification({
        message: 'Missing user context',
        level: notificationStates.warning,
      });
      return;
    }
    const defaultCompanyOption =
      selectedCompany ||
      companyOptions.find((c) => c.value === activeCompanyId) ||
      null;
    setAddToAnotherTeamUser(editRow);
    setAddToAnotherTeamCompany(defaultCompanyOption);
    setAddToAnotherTeamRole(null);
    setAddToAnotherTeamTeams([]);
    setAddToAnotherTeamCandidateTeams([]);
    setAddToAnotherTeamCompanyTeamCount(0);
    setAddToAnotherTeamDialogVisible(true);
    menuRef.current?.hide();
  }, [editRow, selectedCompany, companyOptions, activeCompanyId]);

  useEffect(() => {
    if (!addToAnotherTeamDialogVisible) return;
    if (!addToAnotherTeamUser?.userId || !addToAnotherTeamCompany?.value)
      return;
    loadAddToAnotherTeamCandidates({
      companyId: addToAnotherTeamCompany.value,
      userId: addToAnotherTeamUser.userId,
    });
  }, [
    addToAnotherTeamDialogVisible,
    addToAnotherTeamCompany?.value,
    addToAnotherTeamUser?.userId,
    loadAddToAnotherTeamCandidates,
  ]);

  useEffect(() => {
    if (!addToAnotherTeamDialogVisible || !addToAnotherTeamUser?.userId) {
      return;
    }
    let cancelled = false;
    const uid = addToAnotherTeamUser.userId;

    (async () => {
      setLoadingAddToAnotherTeamActiveSection(true);
      setAddToAnotherTeamActiveBaseline(null);
      try {
        const [activeSettings, companiesRes] = await Promise.all([
          getMemberActiveSettings(uid),
          fetchUserCompanies(uid),
        ]);
        if (cancelled) return;

        if (!companiesRes.success || !companiesRes.data?.length) {
          setAddToAnotherTeamActiveCompanyOptions([]);
          setAddToAnotherTeamActiveCompany(null);
          setAddToAnotherTeamActiveTeamOptions([]);
          setAddToAnotherTeamActiveTeam(null);
          return;
        }

        const companyOpts = companiesRes.data.map((c) => ({
          label: c.companyName || c.companyId,
          value: c.companyId,
        }));
        setAddToAnotherTeamActiveCompanyOptions(companyOpts);

        const validIds = new Set(companyOpts.map((o) => o.value));
        const fromMemberDoc =
          activeSettings.activeCompanyId &&
          validIds.has(activeSettings.activeCompanyId)
            ? activeSettings.activeCompanyId
            : null;
        const pageCompanyId = selectedCompany?.value;
        const fromPage =
          pageCompanyId && validIds.has(pageCompanyId) ? pageCompanyId : null;
        const companyId = fromMemberDoc || fromPage || companyOpts[0].value;
        const companyOption =
          companyOpts.find((o) => o.value === companyId) || companyOpts[0];
        setAddToAnotherTeamActiveCompany(companyOption);

        const teams = await getMemberTeamsForCompany(uid, companyOption.value);
        if (cancelled) return;
        const teamOpts = buildTeamOptions(teams);
        setAddToAnotherTeamActiveTeamOptions(teamOpts);

        if (!teamOpts.length) {
          setAddToAnotherTeamActiveTeam(null);
          setAddToAnotherTeamActiveBaseline({
            companyId: companyOption.value,
            teamId: null,
          });
          return;
        }
        const pick =
          activeSettings.activeTeamId &&
          teamOpts.some((t) => t.value === activeSettings.activeTeamId)
            ? activeSettings.activeTeamId
            : teamOpts[0].value;
        const selected = teamOpts.find((t) => t.value === pick) || teamOpts[0];
        setAddToAnotherTeamActiveTeam(selected);
        setAddToAnotherTeamActiveBaseline({
          companyId: companyOption.value,
          teamId: selected.value,
        });
      } catch (e) {
        if (!cancelled) {
          console.error('[AddToAnotherTeam active section]', e);
          setAddToAnotherTeamActiveCompanyOptions([]);
          setAddToAnotherTeamActiveCompany(null);
          setAddToAnotherTeamActiveTeamOptions([]);
          setAddToAnotherTeamActiveTeam(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingAddToAnotherTeamActiveSection(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedCompany read on open only; omit so page filter changes do not reset modal
  }, [
    addToAnotherTeamDialogVisible,
    addToAnotherTeamUser?.userId,
    getMemberActiveSettings,
    getMemberTeamsForCompany,
    buildTeamOptions,
  ]);

  const onAddToAnotherTeamActiveCompanyChange = useCallback(
    async (option) => {
      if (!addToAnotherTeamUser?.userId || !option?.value) return;
      setAddToAnotherTeamActiveCompany(option);
      setAddToAnotherTeamActiveTeam(null);
      setLoadingAddToAnotherTeamActiveSection(true);
      try {
        const teams = await getMemberTeamsForCompany(
          addToAnotherTeamUser.userId,
          option.value
        );
        const teamOpts = buildTeamOptions(teams);
        setAddToAnotherTeamActiveTeamOptions(teamOpts);
        setAddToAnotherTeamActiveTeam(teamOpts[0] || null);
      } finally {
        setLoadingAddToAnotherTeamActiveSection(false);
      }
    },
    [addToAnotherTeamUser?.userId, getMemberTeamsForCompany, buildTeamOptions]
  );

  const submitAddToAnotherTeam = useCallback(async () => {
    if (!addToAnotherTeamUser?.userId || !addToAnotherTeamCompany?.value) {
      addNotification({
        message: 'Please select company and user',
        level: notificationStates.warning,
      });
      return;
    }
    if (!addToAnotherTeamRole?.value) {
      addNotification({
        message: 'Please select access type',
        level: notificationStates.warning,
      });
      return;
    }

    const isAdminPromotion = addToAnotherTeamRole.value === 'Admin';
    if (!isAdminPromotion && addToAnotherTeamTeams.length === 0) {
      addNotification({
        message: 'Please select at least one team',
        level: notificationStates.warning,
      });
      return;
    }

    setSubmittingAddToAnotherTeam(true);
    try {
      const user = firebase.auth().currentUser;
      if (!user) throw new Error('User not authenticated');
      const idToken = await user.getIdToken();
      if (isAdminPromotion) {
        const response = await httpClient.post(
          '/promoteUserToCompanyAdmin',
          {
            userId: addToAnotherTeamUser.userId,
            companyId: addToAnotherTeamCompany.value,
          },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        if (!response?.data?.success) {
          throw new Error(response?.data?.message || 'Failed to promote user');
        }
      } else {
        const response = await httpClient.post(
          '/addUserToTeams',
          {
            userId: addToAnotherTeamUser.userId,
            companyId: addToAnotherTeamCompany.value,
            teamIds: addToAnotherTeamTeams.map((t) => t.value),
            role: addToAnotherTeamRole.value,
          },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        if (!response?.data?.success) {
          throw new Error(
            response?.data?.message || 'Failed to add user to teams'
          );
        }
      }

      addNotification({
        message:
          addToAnotherTeamRole.value === 'Admin'
            ? 'User promoted to Admin and synced across company teams.'
            : 'User added to selected teams successfully.',
        level: notificationStates.success,
      });

      const baseline = addToAnotherTeamActiveBaseline;
      const wantsActiveUpdate =
        baseline &&
        addToAnotherTeamActiveCompany?.value &&
        addToAnotherTeamActiveTeam?.value &&
        (addToAnotherTeamActiveCompany.value !== baseline.companyId ||
          String(addToAnotherTeamActiveTeam.value ?? '') !==
            String(baseline.teamId ?? ''));

      if (wantsActiveUpdate) {
        const activeRes = await teamDatabase.updateMemberActiveCompanyAndTeam(
          addToAnotherTeamUser.userId,
          addToAnotherTeamActiveCompany.value,
          addToAnotherTeamActiveTeam.value
        );
        if (!activeRes?.success) {
          addNotification({
            message:
              activeRes?.error?.message ||
              activeRes?.error ||
              'Membership updated, but active company/team could not be saved.',
            level: notificationStates.warning,
          });
        } else {
          addNotification({
            message: `Active company and team updated for time and activity: ${addToAnotherTeamActiveTeam.label}.`,
            level: notificationStates.success,
          });
          const emailRes = await teamDatabase.sendActiveTeamChangeEmail({
            companyName: addToAnotherTeamActiveCompany.label || '',
            teamName: addToAnotherTeamActiveTeam.label || '',
          });
          if (!emailRes?.success) {
            addNotification({
              message:
                'Active team saved, but the confirmation email could not be sent.',
              level: notificationStates.warning,
            });
          }
        }
        queryClient.invalidateQueries(
          queryKeys.employeeList.extendedDetails(addToAnotherTeamUser.userId)
        );
      }

      invalidateEmployeeListData();
      resetAddToAnotherTeamDialog();
    } catch (e) {
      addNotification({
        message:
          e?.response?.data?.message ||
          e?.message ||
          'Failed to add user to another team',
        level: notificationStates.error,
      });
    } finally {
      setSubmittingAddToAnotherTeam(false);
    }
  }, [
    addToAnotherTeamUser,
    addToAnotherTeamCompany,
    addToAnotherTeamRole,
    addToAnotherTeamTeams,
    addToAnotherTeamActiveBaseline,
    addToAnotherTeamActiveCompany,
    addToAnotherTeamActiveTeam,
    invalidateEmployeeListData,
    resetAddToAnotherTeamDialog,
    queryClient,
  ]);

  const actionsMenuModel = [
    {
      label: 'More details',
      icon: 'pi pi-info-circle',
      command: openMoreDetailsDialog,
    },
    { label: 'Edit', icon: 'pi pi-pencil', command: openEditDialog },
    {
      label: 'Add to another team',
      icon: 'pi pi-user-plus',
      command: openAddToAnotherTeamDialog,
    },
    {
      label: 'Change active team',
      icon: 'pi pi-compass',
      command: openChangeActiveTeamDialog,
    },
    {
      label: 'Remove from team',
      icon: 'pi pi-trash',
      command: openDeleteConfirm,
    },
    {
      label: 'Remove from company',
      icon: 'pi pi-building',
      command: openRemoveFromCompanyConfirm,
    },
  ];

  const editFormInitialValues = useMemo(() => {
    if (!editRow || !extendedDetails) {
      return { name: '', deskId: '', timeZone: '', role: '' };
    }
    return {
      name:
        editRow.name === 'N/A' || editRow.name == null
          ? ''
          : String(editRow.name),
      deskId:
        extendedDetails.deskId === 'N/A' || !extendedDetails.deskId
          ? ''
          : String(extendedDetails.deskId),
      timeZone:
        extendedDetails.timeZone === 'N/A'
          ? ''
          : String(extendedDetails.timeZone),
      role: editRow.role === 'N/A' ? '' : editRow.role,
    };
  }, [editRow, extendedDetails]);
  const editFormCurrentValues = {
    name: editName,
    deskId: editDeskId,
    timeZone: editTimeZone,
    role: editRole,
  };
  const { hasChanges: hasEditFormChanges } = useFormChanges(
    editFormInitialValues,
    editFormCurrentValues,
    ['name', 'deskId', 'timeZone', 'role']
  );

  const saveEditDetails = async () => {
    if (!editRow?.userId || !selectedCompany?.value || !selectedTeam?.value) {
      addNotification({
        message: 'Missing context (user, company or team)',
        level: notificationStates.warning,
      });
      return;
    }
    if (!extendedDetails) {
      addNotification({
        message: 'Still loading member details. Try again.',
        level: notificationStates.warning,
      });
      return;
    }
    const currentDeskId =
      extendedDetails.deskId === 'N/A' || !extendedDetails.deskId
        ? ''
        : String(extendedDetails.deskId);
    const currentTimeZone =
      extendedDetails.timeZone === 'N/A' || !extendedDetails.timeZone
        ? ''
        : String(extendedDetails.timeZone);
    const currentRole = editRow.role === 'N/A' ? '' : editRow.role || '';
    const currentName =
      editRow.name === 'N/A' || editRow.name == null
        ? ''
        : String(editRow.name).trim();
    const newName = (editName || '').trim();
    const newDeskId = (editDeskId || '').trim();
    const newTimeZone = (editTimeZone || '').trim();
    const newRole =
      editRole && ROLE_OPTIONS.some((o) => o.value === editRole)
        ? editRole
        : null;
    const payload = {
      userId: editRow.userId,
      companyId: selectedCompany?.value,
      teamId: selectedTeam?.value,
    };
    if (newName !== currentName) {
      if (!newName) {
        addNotification({
          message: 'Name cannot be empty',
          level: notificationStates.warning,
        });
        return;
      }
      payload.name = newName;
    }
    if (newDeskId !== currentDeskId) payload.deskId = newDeskId;
    if (newTimeZone !== currentTimeZone && newTimeZone)
      payload.timeZone = newTimeZone;
    if (newRole !== null && newRole !== currentRole) payload.role = newRole;
    const hasChanges =
      payload.deskId !== undefined ||
      payload.timeZone !== undefined ||
      payload.role !== undefined ||
      payload.name !== undefined;
    if (!hasChanges) {
      addNotification({
        message: 'No changes to save',
        level: notificationStates.warning,
      });
      return;
    }
    if (payload.deskId !== undefined && !payload.deskId) {
      addNotification({
        message: 'Desk Id cannot be empty when changing',
        level: notificationStates.warning,
      });
      return;
    }
    const emailToSend =
      editRow.email && editRow.email !== 'N/A' ? editRow.email.trim() : null;
    if (!stealthLoading && stealthMode && payload.role !== undefined) {
      pendingStealthAccessSyncRef.current = {
        userId: editRow.userId,
        companyId: selectedCompany.value,
        emailForPasswordReset: emailToSend,
      };
    } else {
      pendingStealthAccessSyncRef.current = null;
    }

    dispatch({
      type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS,
      payload,
    });

    if (newRole !== currentRole && emailToSend) {
      try {
        const functions = firebase.functions();
        const sendMailForEmployeeRoleChange = functions.httpsCallable(
          'sendMailForEmployeeRoleChange'
        );
        const result = await sendMailForEmployeeRoleChange({
          email: emailToSend,
          newRole,
          currentRole,
          companyName: selectedCompany?.label,
          teamName: selectedTeam?.label,
          userName: editRow.name,
        });
      } catch (error) {
        console.error(error);
        addNotification({
          message: 'Failed to send email for employee role change',
          level: notificationStates.error,
        });
      }
    }
  };

  const renderGuidWithCopy = (guid) => {
    if (guid === 'N/A' || !guid) {
      return <span>N/A</span>;
    }
    return (
      <div className="d-flex align-items-center">
        <span
          className="mr-2"
          style={{ fontFamily: 'monospace', fontSize: '12px' }}
        >
          {guid}
        </span>
        <PrimeButton
          icon="pi pi-copy"
          className="p-button-text p-button-sm"
          onClick={() => copyGuid(guid)}
          tooltip="Copy GUID"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  if (error && userProfile?.activeCompany?.value !== 'Admin') {
    return (
      <Container className="mt-4">
        <Alert color="danger">{error}</Alert>
      </Container>
    );
  }
  return (
    <>
      <div className={styles.employeeListPage}>
        <div className={styles.employeeListContainer}>
          <div className={styles.employeeListHeader}>
            <div className={styles.employeeListHeaderTextContainer}>
              <Text
                variant={textVariantsEnum.Header}
                fontWeight={fontWeightEnum.Normal}
              >
                Employee Page
              </Text>
              <Text
                variant={textVariantsEnum.Text_xs}
                fontWeight={fontWeightEnum.Normal}
                color={'gray-600'}
              >
                View employees with their license information
              </Text>
            </div>
            <div className={styles.employeeListHeaderActions}>
              {showAddTeamButton ? (
                <>
                  <ButtonComponent
                    text="Invite Members"
                    onClick={() =>
                      history.push({
                        pathname: appRoutes().addEmployee,
                        search: mergeEmployeePageFiltersIntoSearch(
                          location.search,
                          {
                            companyId: selectedCompany?.value ?? '',
                            teamId: selectedTeam?.value ?? '',
                          }
                        ),
                      })
                    }
                    variant={buttonVariants.rounded}
                  />
                  <ButtonComponent
                    text={'Add team'}
                    onClick={() => setShowAddTeamModal(true)}
                    variant={buttonVariants.rounded}
                  />
                </>
              ) : null}
              <ButtonComponent
                text={'Export to CSV'}
                onClick={exportToCSV}
                variant={buttonVariants.primary}
              />
            </div>
          </div>
          <div className={styles.employeeListDropdownContainer}>
            <Dropdown
              label="Company *"
              value={selectedCompany}
              selectStyles={borderedSelectStyles}
              options={companyOptions}
              onChange={(option) => {
                setSelectedCompany(option);
                setSelectedTeam(null);
                setError(null);
              }}
              placeholder={
                loadingCompanies ? 'Loading companies...' : 'Select Company'
              }
            />
            <Dropdown
              label="Department *"
              value={selectedTeam}
              selectStyles={borderedSelectStyles}
              options={teamOptions}
              onChange={(option) => {
                setSelectedTeam(option);
                setError(null);
              }}
              placeholder={loadingTeams ? 'Loading teams...' : 'Select Team'}
              disabled={!selectedCompany || loadingTeams}
            />
          </div>
          <div className={styles.tabBar}>
            <button
              className={`${styles.tab} ${activeTab === 'employees' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('employees')}
            >
              Employees
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'quickbooks' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('quickbooks')}
            >
              QuickBooks
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'salesforce' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('salesforce')}
            >
              Salesforce
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'sagehr' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('sagehr')}
            >
              Sage HR
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'zohopeople' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('zohopeople')}
            >
              Zoho People
            </button>
          </div>
          <div className={styles.employeeListTableContainer}>
            <Toast ref={toast} />
            <ConfirmDialog />
            <Menu model={actionsMenuModel} popup ref={menuRef} />
            <Dialog
              header="Employee details"
              visible={Boolean(moreDetailsUserId)}
              style={{ width: '440px' }}
              onHide={() => setMoreDetailsUserId(null)}
              footer={
                <CustomButton
                  text="Close"
                  variant={buttonVariants.secondary}
                  onClick={() => setMoreDetailsUserId(null)}
                />
              }
            >
              {moreDetailsRosterRow ? (
                <div className={styles.editFormContainer}>
                  <CustomInput
                    label="Name"
                    type="text"
                    value={moreDetailsRosterRow.name}
                    readOnly
                    disabled
                  />
                  <CustomInput
                    label="Email"
                    type="text"
                    value={moreDetailsRosterRow.email}
                    readOnly
                    disabled
                  />
                  <CustomInput
                    label="Role"
                    type="text"
                    value={moreDetailsRosterRow.role}
                    readOnly
                    disabled
                  />
                  {loadingExtendedDetails && moreDetailsUserId ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="mt-2 mb-0 small text-muted">
                        Loading additional details…
                      </p>
                    </div>
                  ) : extendedDetailsError && moreDetailsUserId ? (
                    <Alert color="danger" className="mt-2 mb-0">
                      {extendedDetailsQueryError?.message ||
                        'Failed to load additional details'}
                    </Alert>
                  ) : extendedDetails ? (
                    <>
                      <CustomInput
                        label="Employee Id"
                        type="text"
                        value={extendedDetails.employeeId}
                        readOnly
                        disabled
                      />
                      <CustomInput
                        label="Time zone"
                        type="text"
                        value={extendedDetails.timeZone}
                        readOnly
                        disabled
                      />
                      <Label className="d-block">GUID</Label>
                      <div className="mb-3">
                        {renderGuidWithCopy(extendedDetails.guid)}
                      </div>
                      <CustomInput
                        label="Desk Id"
                        type="text"
                        value={extendedDetails.deskId}
                        readOnly
                        disabled
                      />
                    </>
                  ) : null}
                </div>
              ) : null}
            </Dialog>
            <Dialog
              header="Add to another team"
              visible={addToAnotherTeamDialogVisible}
              style={{ width: '540px' }}
              onHide={resetAddToAnotherTeamDialog}
              footer={
                <div>
                  <CustomButton
                    text="Cancel"
                    variant={buttonVariants.secondary}
                    onClick={resetAddToAnotherTeamDialog}
                    disabled={submittingAddToAnotherTeam}
                  />
                  <CustomButton
                    text={
                      addToAnotherTeamRole?.value === 'Admin'
                        ? 'Promote to Admin'
                        : 'Add to team'
                    }
                    variant={buttonVariants.primary}
                    onClick={submitAddToAnotherTeam}
                    loading={submittingAddToAnotherTeam}
                    disabled={
                      submittingAddToAnotherTeam ||
                      loadingAddToAnotherTeamCandidates ||
                      loadingAddToAnotherTeamActiveSection ||
                      !addToAnotherTeamCompany?.value ||
                      !addToAnotherTeamRole?.value ||
                      (addToAnotherTeamRole?.value !== 'Admin' &&
                        addToAnotherTeamTeams.length === 0)
                    }
                  />
                </div>
              }
            >
              <div className={styles.editFormContainer}>
                <CustomInput
                  label="Email"
                  type="text"
                  value={addToAnotherTeamUser?.email || ''}
                  readOnly
                  disabled
                />

                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Medium}
                  color={'gray-800'}
                  className={styles.addToAnotherTeamModalSectionTitle}
                >
                  Add to company and teams (membership)
                </Text>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Normal}
                  color={'gray-600'}
                >
                  Choose which company and teams this user will be added to.
                  This updates team membership only, not their default for
                  logging time.
                </Text>

                <Dropdown
                  label="Company to add them to *"
                  value={addToAnotherTeamCompany}
                  selectStyles={borderedSelectStyles}
                  options={companyOptions}
                  onChange={(option) => {
                    setAddToAnotherTeamCompany(option);
                    setAddToAnotherTeamTeams([]);
                  }}
                  placeholder="Select company"
                  isDisabled={submittingAddToAnotherTeam}
                />
                <Dropdown
                  label="Access type *"
                  value={addToAnotherTeamRole}
                  selectStyles={borderedSelectStyles}
                  options={ROLE_OPTIONS}
                  onChange={(option) => setAddToAnotherTeamRole(option)}
                  placeholder="Select access type"
                  isDisabled={submittingAddToAnotherTeam}
                />
                {addToAnotherTeamRole?.value === 'Admin' ? (
                  <div className={styles.adminPromotionNote}>
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Normal}
                      color={'gray-600'}
                    >
                      Admin is company-wide. User will be added to all teams in
                      this company.
                    </Text>
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Medium}
                      color={'gray-700'}
                    >
                      {`Will add to up to ${addToAnotherTeamCompanyTeamCount} team(s).`}
                    </Text>
                  </div>
                ) : (
                  <Dropdown
                    label="Team(s) to add them to *"
                    value={addToAnotherTeamTeams}
                    selectStyles={borderedSelectStyles}
                    options={addToAnotherTeamCandidateTeams}
                    onChange={(option) =>
                      setAddToAnotherTeamTeams(
                        Array.isArray(option) ? option : []
                      )
                    }
                    isMulti
                    placeholder={
                      loadingAddToAnotherTeamCandidates
                        ? 'Loading teams...'
                        : 'Select one or more teams'
                    }
                    isDisabled={
                      submittingAddToAnotherTeam ||
                      loadingAddToAnotherTeamCandidates
                    }
                  />
                )}
                {!loadingAddToAnotherTeamCandidates &&
                  addToAnotherTeamRole?.value !== 'Admin' &&
                  addToAnotherTeamCandidateTeams.length === 0 && (
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Normal}
                      color={'gray-600'}
                    >
                      User is already in all teams for this company.
                    </Text>
                  )}

                <div className={styles.addToAnotherTeamModalDivider} />

                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Medium}
                  color={'gray-800'}
                  className={styles.addToAnotherTeamModalSectionTitle}
                >
                  Active company and team (time and activity)
                </Text>
                <div className={styles.addToAnotherTeamActiveInfo}>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Normal}
                    color={'gray-700'}
                  >
                    Active company and team is which company and team their time
                    logs and activity data is stored.
                  </Text>
                </div>

                {loadingAddToAnotherTeamActiveSection &&
                !addToAnotherTeamActiveCompanyOptions.length ? (
                  <div className="text-center py-3">
                    <Spinner color="primary" size="sm" />
                    <p className="mt-2 mb-0 small text-muted">
                      Loading active company and team…
                    </p>
                  </div>
                ) : addToAnotherTeamActiveCompanyOptions.length === 0 ? (
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Normal}
                    color={'gray-600'}
                  >
                    No companies on file for this member; active company and
                    team cannot be edited here.
                  </Text>
                ) : (
                  <>
                    <Dropdown
                      label="Active company (for time and activity) *"
                      value={addToAnotherTeamActiveCompany}
                      selectStyles={borderedSelectStyles}
                      options={addToAnotherTeamActiveCompanyOptions}
                      onChange={onAddToAnotherTeamActiveCompanyChange}
                      placeholder="Select active company"
                      isDisabled={
                        submittingAddToAnotherTeam ||
                        loadingAddToAnotherTeamActiveSection
                      }
                    />
                    <Dropdown
                      label="Active team (for time and activity) *"
                      value={addToAnotherTeamActiveTeam}
                      selectStyles={borderedSelectStyles}
                      options={addToAnotherTeamActiveTeamOptions}
                      onChange={(opt) => setAddToAnotherTeamActiveTeam(opt)}
                      placeholder={
                        loadingAddToAnotherTeamActiveSection
                          ? 'Loading teams…'
                          : 'Select active team'
                      }
                      isDisabled={
                        submittingAddToAnotherTeam ||
                        loadingAddToAnotherTeamActiveSection ||
                        !addToAnotherTeamActiveTeamOptions.length
                      }
                    />
                    {!loadingAddToAnotherTeamActiveSection &&
                      addToAnotherTeamActiveCompany &&
                      addToAnotherTeamActiveTeamOptions.length === 0 && (
                        <Text
                          variant={textVariantsEnum.Text_sm}
                          fontWeight={fontWeightEnum.Normal}
                          color={'gray-600'}
                        >
                          This member has no teams in the selected active
                          company.
                        </Text>
                      )}
                  </>
                )}
              </div>
            </Dialog>
            <Dialog
              header="Change active team"
              visible={changeActiveTeamDialogVisible}
              style={{ width: '480px' }}
              onHide={resetChangeActiveTeamDialog}
              footer={
                <div>
                  <CustomButton
                    text="Cancel"
                    variant={buttonVariants.secondary}
                    onClick={resetChangeActiveTeamDialog}
                    disabled={submittingChangeActiveTeam}
                  />
                  <CustomButton
                    text="Save"
                    variant={buttonVariants.primary}
                    onClick={submitChangeActiveTeam}
                    loading={submittingChangeActiveTeam}
                    disabled={
                      submittingChangeActiveTeam ||
                      loadingChangeActiveTeamModal ||
                      !changeActiveTeamCompany?.value ||
                      !changeActiveTeamTeam?.value ||
                      !hasChangeActiveTeamPendingSave
                    }
                  />
                </div>
              }
            >
              <div className={styles.editFormContainer}>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Normal}
                  color={'gray-600'}
                >
                  Time logs and other activity for this employee are stored
                  under their active company and team. Use the selections below
                  to choose which company and team that data should follow.
                </Text>
                <div style={{ height: 12 }} />
                <CustomInput
                  label="Email"
                  type="text"
                  value={changeActiveTeamUser?.email || ''}
                  readOnly
                  disabled
                />
                {loadingChangeActiveTeamModal &&
                !changeActiveTeamCompanyOptions.length ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                    <p className="mt-2 mb-0 small text-muted">Loading…</p>
                  </div>
                ) : (
                  <>
                    <Dropdown
                      label="Company *"
                      value={changeActiveTeamCompany}
                      selectStyles={borderedSelectStyles}
                      options={changeActiveTeamCompanyOptions}
                      onChange={onChangeActiveTeamCompanySelect}
                      placeholder="Select company"
                      isDisabled={
                        submittingChangeActiveTeam ||
                        loadingChangeActiveTeamModal
                      }
                    />
                    <Dropdown
                      label="Active team *"
                      value={changeActiveTeamTeam}
                      selectStyles={borderedSelectStyles}
                      options={changeActiveTeamTeamOptions}
                      onChange={(opt) => setChangeActiveTeamTeam(opt)}
                      placeholder={
                        loadingChangeActiveTeamModal
                          ? 'Loading teams…'
                          : 'Select team'
                      }
                      isDisabled={
                        submittingChangeActiveTeam ||
                        loadingChangeActiveTeamModal ||
                        !changeActiveTeamTeamOptions.length
                      }
                    />
                    {!loadingChangeActiveTeamModal &&
                      changeActiveTeamCompany &&
                      changeActiveTeamTeamOptions.length === 0 && (
                        <Text
                          variant={textVariantsEnum.Text_sm}
                          fontWeight={fontWeightEnum.Normal}
                          color={'gray-600'}
                        >
                          This member has no teams in the selected company.
                        </Text>
                      )}
                  </>
                )}
              </div>
            </Dialog>
            <Dialog
              header="Edit Employee Details"
              visible={editDialogVisible}
              style={{ width: '420px' }}
              onHide={() => {
                setEditDialogVisible(false);
                setEditRow(null);
                setEditName('');
                setEditDeskId('');
                setEditTimeZone('');
                setEditRole('');
                editFormHydratedRef.current = null;
              }}
              footer={
                <div>
                  <CustomButton
                    text="Cancel"
                    variant={buttonVariants.secondary}
                    onClick={() => {
                      setEditDialogVisible(false);
                      editFormHydratedRef.current = null;
                    }}
                  />
                  <CustomButton
                    text="Save"
                    variant={buttonVariants.primary}
                    onClick={saveEditDetails}
                    loading={updateEmployeeDetailsPending}
                    disabled={
                      updateEmployeeDetailsPending ||
                      !hasEditFormChanges ||
                      !extendedDetails ||
                      loadingExtendedDetails
                    }
                  />
                </div>
              }
            >
              {editRow && (
                <>
                  {loadingExtendedDetails ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="mt-2 mb-0">Loading member details…</p>
                    </div>
                  ) : extendedDetailsError ? (
                    <Alert color="danger">
                      {extendedDetailsQueryError?.message ||
                        'Failed to load member details'}
                    </Alert>
                  ) : extendedDetails ? (
                    <div className={styles.editFormContainer}>
                      {/* Name edit hidden from UI — restore block to allow editing from Employee List again
                      <CustomInput
                        label="Name"
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Enter name"
                      />
                      */}

                      <CustomInput
                        label="Email"
                        type="text"
                        value={editRow.email}
                        readOnly
                        disabled
                      />

                      <CustomInput
                        label="Employee Id"
                        type="text"
                        value={extendedDetails.employeeId}
                        readOnly
                        disabled
                      />

                      <CustomInput
                        label="Desk Id"
                        type="text"
                        value={editDeskId}
                        onChange={(e) => setEditDeskId(e.target.value)}
                        placeholder="Enter desk Id"
                      />

                      <Dropdown
                        selectStyles={borderedSelectStyles}
                        label={'Time Zone'}
                        value={{ label: editTimeZone, value: editTimeZone }}
                        options={timezoneOptions}
                        onChange={(e) => setEditTimeZone(e.value || '')}
                        placeholder="Select Time Zone"
                        filter
                        className="w-100"
                      />

                      <Dropdown
                        selectStyles={borderedSelectStyles}
                        label={'Role'}
                        value={{ label: editRole, value: editRole }}
                        options={ROLE_OPTIONS}
                        onChange={(e) => setEditRole(e.value || '')}
                        placeholder="Select Role"
                        className="w-100"
                      />
                    </div>
                  ) : null}
                </>
              )}
            </Dialog>
            <Dialog
              header="Select new active team"
              visible={pickActiveTeamDialogVisible}
              style={{ width: '420px' }}
              onHide={() => {
                resetPickActiveTeamDialog();
              }}
              footer={
                <div>
                  <CustomButton
                    text="Cancel"
                    variant={buttonVariants.secondary}
                    onClick={resetPickActiveTeamDialog}
                    disabled={removeUserFromTeamPending}
                  />
                  <CustomButton
                    text="Continue"
                    variant={buttonVariants.primary}
                    onClick={confirmDeleteAfterPickingActiveTeam}
                    loading={removeUserFromTeamPending}
                    disabled={
                      removeUserFromTeamPending ||
                      !selectedReplacementTeam?.value
                    }
                  />
                </div>
              }
            >
              <div className={styles.editFormContainer}>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Normal}
                  color={'gray-600'}
                >
                  This user is currently being removed from their active team.
                  Choose which team should become active before deleting.
                </Text>
                <div style={{ height: 10 }} />
                <Dropdown
                  label="New active team *"
                  value={selectedReplacementTeam}
                  selectStyles={borderedSelectStyles}
                  options={replacementTeamOptions}
                  onChange={(opt) => setSelectedReplacementTeam(opt)}
                  placeholder="Select Team"
                  isDisabled={removeUserFromTeamPending}
                />
              </div>
            </Dialog>
            {(error || companiesError || teamsError || rosterError) && (
              <Alert color="danger" className="mb-4">
                {error ||
                  companiesError ||
                  teamsError ||
                  (rosterQueryError?.message
                    ? String(rosterQueryError.message)
                    : 'Failed to load team roster.')}
              </Alert>
            )}

            {activeTab === 'employees' ? (
              <>
                {/* Search - only when we have employees */}
                {selectedTeam && employees.length > 0 && !loadingEmployees && (
                  <FormGroup className="mb-3">
                    <CustomInput
                      label="Search employees"
                      id="employee-search"
                      type="text"
                      placeholder="Search by name, email, or role..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm.trim() && (
                      <small className="text-muted">
                        Showing {filteredEmployees.length} of {employees.length}{' '}
                        employees
                      </small>
                    )}
                  </FormGroup>
                )}

                {/* Employee Table */}
                {loadingEmployees ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading employees...</p>
                  </div>
                ) : selectedTeam && employees.length > 0 ? (
                  <div className={styles.employeeListTable}>
                    <DataTable
                      className="prime-react-table"
                      value={filteredEmployees}
                      responsiveLayout="scroll"
                      paginator
                      paginatorClassName="prime-react-paginator"
                      rows={10}
                      header
                      rowsPerPageOptions={[10, 25, 50]}
                      emptyMessage={
                        searchTerm.trim()
                          ? 'No employees match your search.'
                          : 'No employees found'
                      }
                    >
                      <Column
                        field="name"
                        header="Name"
                        sortable
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="email"
                        header="Email"
                        sortable
                        style={{ minWidth: '200px' }}
                      />
                      <Column
                        field="role"
                        header="Role"
                        sortable
                        style={{ minWidth: '100px' }}
                      />
                      <Column
                        header="Actions"
                        body={(rowData) => (
                          <PrimeButton
                            icon="pi pi-ellipsis-v"
                            className="p-button-text p-button-rounded"
                            onClick={(e) => onActionsButtonClick(e, rowData)}
                            aria-label="Actions"
                          />
                        )}
                        style={{ width: '80px' }}
                      />
                    </DataTable>
                  </div>
                ) : selectedTeam && employees.length === 0 ? (
                  <Alert color="info">
                    No employees found for the selected team.
                  </Alert>
                ) : (
                  <Alert
                    style={{
                      backgroundColor: '#1F74EC',
                    }}
                  >
                    Please select a company and team to view employees.
                  </Alert>
                )}
              </>
            ) : (
              <IntegrationImportTab
                companyId={selectedCompany?.value}
                teamId={selectedTeam?.value}
                userId={userUID}
                source={activeTab}
              />
            )}
          </div>
        </div>
      </div>
      <AddTeamModal
        isVisible={showAddTeamModal}
        onClose={() => setShowAddTeamModal(false)}
        callback={handleTeamCreated}
        defaultCompanyId={addTeamDefaultCompanyId}
      />
    </>
  );
};
